/**
 * The partnership representative (PR) a multi-member LLC's Form 1065 names
 * when it cannot elect out of the centralized audit regime (NT-61). The
 * customer reads a notice about the PR and accepts it before paying; the API
 * gate, the customer dialog and the admin console all read this module, so
 * they agree on who needs the notice and what it said.
 *
 * Sources: Reg. 301.6223-1/-2 (the PR's sole authority binds the partnership
 * and every partner), i8979 (only a person who was a partner can revoke the
 * designation, on Form 8979 Part I), i1065 (2025) p. 30 (an election out only
 * on a timely return, with a correct U.S. TIN for every partner).
 */
import {
  bbaElectionOutEligibility,
  individualUsTinOf,
  type BbaPartner,
} from "./bba-election";
import { filingPhase } from "./deadlines";
import type { FederalTaxFilingMode } from "./pricing";
import type { EntityKind } from "./scenario";

/** A member on the company record: the tax id and the type the record gives it. */
export interface RepresentativeMember {
  tin?: string | null;
  tinType?: string | null;
}

export interface RepresentativeNeedInput {
  kind: EntityKind;
  /** The members on file; empty or absent is unknown, and unknown needs the notice. */
  members?: readonly RepresentativeMember[];
  filingMode?: FederalTaxFilingMode;
  /** Without a tax year the timeliness is unknown, and unknown needs the notice. */
  taxYear?: number;
  now?: Date;
}

/** The digits of every member tax id the record types as an EIN (never an individual's). */
function einsOnFile(members: readonly RepresentativeMember[]): Set<string> {
  return new Set(
    members
      .filter(
        (m) =>
          String(m.tinType ?? "")
            .trim()
            .toUpperCase() === "EIN",
      )
      .map((m) => String(m.tin ?? "").replace(/\D/g, ""))
      .filter((digits) => digits.length === 9),
  );
}

/**
 * Late at the moment of purchase: a late filing, or today (IRS day) past the
 * original due date. No order exists yet, so no Form 7004 is approved, the
 * same reading the return model makes of an order without an approved
 * extension (`resolveFilingDeadline`).
 */
function lateAtPurchase(input: RepresentativeNeedInput): boolean {
  if (input.filingMode === "late") return true;
  if (input.taxYear === undefined) return true;
  const phase = filingPhase("mmllc", input.taxYear, input.now ?? new Date());
  return phase.phase !== "standard";
}

/**
 * The return will name the PR: a multi-member LLC that cannot elect out of the
 * audit regime (late, or a member without an individual SSN or ITIN on file).
 * Exactly the negation of `bbaElectionOutEligibility` for the same members.
 */
export function partnershipRepresentativeNeeded(
  input: RepresentativeNeedInput,
): boolean {
  if (input.kind !== "mmllc") return false;
  const members = input.members ?? [];
  const eins = einsOnFile(members);
  // Qualification refuses any partner that is not an individual.
  const partners: BbaPartner[] = members.map((m) => ({
    kind: "individual",
    usTin: individualUsTinOf(
      { tin: m.tin, tinType: m.tinType },
      { einsOnFile: eins },
    ),
  }));
  return !bbaElectionOutEligibility(partners, { late: lateAtPurchase(input) })
    .eligible;
}

/**
 * The notice wording a customer accepts. Never edit a shipped version: add a
 * new one and point the current version at it, so every stored acceptance
 * still reads the exact text it was given.
 */
export interface PartnershipRepresentativeNotice {
  title: string;
  paragraphs: string[];
  checkboxLabel: string;
}

const NOTICES: Record<
  string,
  (name: string) => PartnershipRepresentativeNotice
> = {
  // Owner wording, 2026-10-22. The first draft ("2026-10-22") was never shown to a customer.
  "2026-10-22.2": (name) => ({
    title: "Who speaks for your partnership with the IRS",
    paragraphs: [
      `Your company's Form 1065 will name ${name} as its partnership representative. The IRS asks every partnership return to name one unless the partnership elects out of the partnership audit rules, and this return does not make that election.`,
      `The partnership representative receives IRS letters about this return and acts for your company if the IRS audits it. What the representative agrees with the IRS in that audit binds your company and every member.`,
      `We forward every IRS letter about this return to your company, and the representative acts only on your company's written instruction.`,
      `Your company can replace the representative later by filing Form 8979, and we help you with it.`,
    ],
    checkboxLabel: `I have read this and I accept ${name} as my partnership's representative`,
  }),
};

/** The wording version a new acceptance records. */
export const PARTNERSHIP_REPRESENTATIVE_NOTICE_VERSION = "2026-10-22.2";

/** The notice for a version, or null for one we never shipped. */
export function partnershipRepresentativeNotice(
  version: string,
  representativeName: string,
): PartnershipRepresentativeNotice | null {
  const notice = NOTICES[version];
  return notice ? notice(representativeName) : null;
}

/** What the order stores (`service_data.qualification.partnershipRepresentativeAcceptance`). */
export interface PartnershipRepresentativeAcceptance {
  version: string;
  acceptedAt: string;
  acceptedByUserId: string;
  representativeName: string;
  /**
   * The company's member (shareholder) ids when it was accepted. A changed
   * member list clears the acceptance and the gate asks again (owner,
   * 2026-10-22); `acceptanceCoversMembers` compares them.
   */
  memberIds?: string[];
}

/** The acceptance was given for exactly these members (any order); one without a member list covers none. */
export function acceptanceCoversMembers(
  acceptance: Pick<PartnershipRepresentativeAcceptance, "memberIds">,
  memberIds: readonly string[],
): boolean {
  const accepted = acceptance.memberIds;
  if (!accepted || accepted.length !== memberIds.length) return false;
  const current = new Set(memberIds);
  return (
    current.size === accepted.length && accepted.every((id) => current.has(id))
  );
}

/** A stored acceptance, shape-checked; null for anything else. */
export function parsePartnershipRepresentativeAcceptance(
  raw: unknown,
): PartnershipRepresentativeAcceptance | null {
  let value = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const text = (key: string) =>
    typeof record[key] === "string" && (record[key] as string).trim() !== ""
      ? (record[key] as string)
      : null;
  const version = text("version");
  const acceptedAt = text("acceptedAt");
  const acceptedByUserId = text("acceptedByUserId");
  const representativeName = text("representativeName");
  if (!version || !acceptedAt || !acceptedByUserId || !representativeName) {
    return null;
  }
  const memberIds =
    Array.isArray(record.memberIds) &&
    record.memberIds.every((id) => typeof id === "string")
      ? (record.memberIds as string[])
      : undefined;
  return {
    version,
    acceptedAt,
    acceptedByUserId,
    representativeName,
    ...(memberIds ? { memberIds } : {}),
  };
}
